// /src/pages/ViewTitlesJSONPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// These endpoints now require a JWT server-side. credentials:"include" alone
// is not enough -- auth is a bearer header here, not a cookie.
const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export default function ViewTitlesJSONPage() {
  const [rows, setRows] = useState([]); // raw rows from /api/titles/normalized
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/titles/normalized", {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      const items = Array.isArray(payload.items) ? payload.items : [];
      setRows(items);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Group rows by doc_id (fallback to id) → one card per document
  const grouped = useMemo(() => {
    const byDoc = new Map();

    for (const r of rows) {
      const key = r.doc_id ?? r.id;
      if (key == null) continue;

      if (!byDoc.has(key)) {
        byDoc.set(key, {
          doc_id: r.doc_id,
          id: r.id,
          filename: r.filename,
          doc_type: r.doc_type,

          seller_name: r.seller_name,
          seller_address: r.seller_address,
          seller_city: r.seller_city,
          seller_state: r.seller_state,
          seller_zip_code: r.seller_zip_code,
          seller_license_number: r.seller_license_number,

          buyer_name: r.buyer_name,
          buyer_address: r.buyer_address,
          buyer_city: r.buyer_city,
          buyer_state: r.buyer_state,
          buyer_zip_code: r.buyer_zip_code,
          buyer_license_number: r.buyer_license_number,

          // prefer acquired_date, fall back to date if route still sends that
          acquired_date: r.acquired_date ?? r.date,

          agent: r.agent,
          state: r.state,
          created_dt: r.created_dt,
          modified_dt: r.modified_dt,
          exported_dt: r.exported_dt,

          seller_is_company: r.seller_is_company, // seller flag

          vehicles: [],
        });
      }

      const group = byDoc.get(key);
      const isScrap = r.doc_type === "scrap_inventory";

      group.vehicles.push({
        rowId: r.id, // keep row id for row-level edits
        make: r.make || "",
        model: r.model || "",
        body_style: r.body_style || "",
        year: r.year || "",
        color: r.color || "",
        vin: r.vin || "",
        title_number: r.title_number || "",

        // For convenience:
        //   scrap_inventory: display stock # = seller_stock_number
        //   individual_vehicle: display stock # = new stock_number
        stock_number: isScrap
          ? (r.seller_stock_number || "")
          : (r.stock_number || ""),

        seller_stock_number: r.seller_stock_number || "",
        internal_number: r.internal_number || "",
      });
    }

    const arr = Array.from(byDoc.values());
    arr.sort((a, b) => {
      const da = a.created_dt ? new Date(a.created_dt).getTime() : 0;
      const db = b.created_dt ? new Date(b.created_dt).getTime() : 0;
      return db - da;
    });
    return arr;
  }, [rows]);

  // Filter documents by filename, ids, names, VIN, etc.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return grouped;

    return grouped.filter((g) => {
      const vehicleFields = g.vehicles.flatMap((v) => [
        v.make,
        v.model,
        v.body_style,
        v.year,
        v.color,
        v.vin,
        v.title_number,
        v.stock_number,
        v.internal_number,
        v.seller_stock_number,
      ]);

      const haystacks = [
        g.filename,
        g.doc_id,
        g.id,
        g.doc_type,
        g.seller_name,
        g.buyer_name,
        g.agent,
        g.state,
        g.acquired_date,
        ...vehicleFields,
      ]
        .filter(Boolean)
        .map((x) => String(x).toLowerCase());

      return haystacks.some((h) => h.includes(term));
    });
  }, [grouped, q]);

  // Save handler used by all editable fields
  const handleFieldUpdate = async ({ scope, rowId, docId, field, value }) => {
    // Send PATCH to backend
    const res = await fetch(`/api/titles/${rowId}`, {
      method: "PATCH",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({ field, value, scope, doc_id: docId }),
    });
    if (!res.ok) {
      throw new Error(`Save failed (HTTP ${res.status})`);
    }

    // On success, update local state
    setRows((prev) =>
      prev.map((r) => {
        if (scope === "doc") {
          const sameDoc =
            docId != null ? r.doc_id === docId : r.id === rowId;
          return sameDoc ? { ...r, [field]: value } : r;
        }
        // scope === "row"
        return r.id === rowId ? { ...r, [field]: value } : r;
      })
    );
  };

  // Export handler: submit a whole doc (card) to Excel
  const handleExportDoc = async (docKey) => {
    try {
      setError("");
      const res = await fetch(`/api/titles/doc/${docKey}/export`, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (HTTP ${res.status})`);
      }
      const body = await res.json();

      // Remove all rows for this doc from local state
      setRows((prev) =>
        prev.filter((r) => (r.doc_id ?? r.id) !== docKey)
      );

      console.log(
        `Exported doc ${body.doc_id} (${body.doc_type}), rows=${body.exported_count}`
      );
    } catch (e) {
      console.error(e);
      setError(String(e?.message || e));
    }
  };

  // Delete handler: delete document (and cascades) then remove card
  const handleDeleteDoc = async (docKey) => {
    try {
      setError("");
      const res = await fetch(`/api/documents/${docKey}`, {
        method: "DELETE",
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Delete failed (HTTP ${res.status})`);
      }

      // Remove all rows for this doc from local state
      setRows((prev) =>
        prev.filter((r) => (r.doc_id ?? r.id) !== docKey)
      );
    } catch (e) {
      console.error(e);
      setError(String(e?.message || e));
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
          ← Back
        </Link>
        <h1 className="h4 mb-0">Normalized Titles</h1>
        <div className="ms-auto d-flex gap-2">
          <button
            onClick={fetchAll}
            className="btn btn-outline-primary btn-sm"
          >
            Refresh
          </button>
          <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
            Upload titles
          </Link>
        </div>
      </div>

      <div className="input-group mb-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="form-control"
          placeholder="Search file, doc id, VIN, names…"
        />
        <span className="input-group-text">
          {filtered.length} {filtered.length === 1 ? "document" : "documents"}
        </span>
      </div>

      {loading && (
        <div className="alert alert-secondary mb-3">Loading…</div>
      )}
      {error && <div className="alert alert-danger mb-3">{error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div className="alert alert-warning">
          No titles found from the database.
        </div>
      )}

      <div className="vstack gap-3">
        {filtered.map((group) => (
          <TitleCard
            key={group.doc_id ?? group.id}
            group={group}
            onUpdate={handleFieldUpdate}
            onExportDoc={handleExportDoc}
            onDeleteDoc={handleDeleteDoc}
          />
        ))}
      </div>
    </div>
  );
}

function TitleCard({ group, onUpdate, onExportDoc, onDeleteDoc }) {
  const {
    doc_id,
    id,
    filename,
    doc_type,

    seller_name,
    seller_address,
    seller_city,
    seller_state,
    seller_zip_code,
    seller_license_number,

    buyer_name,
    buyer_address,
    buyer_city,
    buyer_state,
    buyer_zip_code,
    buyer_license_number,

    acquired_date,

    agent,
    state,
    created_dt,
    modified_dt,

    seller_is_company,

    vehicles = [],
  } = group || {};

  const seller = {
    name: seller_name || "",
    address: seller_address || "",
    city: seller_city || "",
    state: seller_state || "",
    zip: seller_zip_code || "",
    license: seller_license_number || "",
  };

  const buyer = {
    name: buyer_name || "",
    address: buyer_address || "",
    city: buyer_city || "",
    state: buyer_state || "",
    zip: buyer_zip_code || "",
    license: buyer_license_number || "",
  };

  const uploadedAt = created_dt || modified_dt || null;
  const displayDate = acquired_date || null;
  const docKey = doc_id ?? id;

  const isScrap = doc_type === "scrap_inventory";
  const hasInternal = doc_type === "individual_vehicle";
  // columns: Make, Model, Body Style, Year, Color, VIN, Title #, Stock #, [Internal #]
  const vehicleColSpan = hasInternal ? 9 : 8;

  // Render a YYYY-MM-DD string as local date (no timezone shift)
  const renderAcquiredDate = (v) => {
    if (!v) return <code>null</code>;
    const [y, m, d] = String(v).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return <code>{v}</code>;
    const js = new Date(y, m - 1, d); // local midnight
    return <code>{js.toLocaleDateString()}</code>;
  };

  return (
    <div
      className="card"
      style={{
        borderRadius: "1rem",
        overflow: "hidden", // keep children inside rounded corners
      }}
    >
      <div className="card-header d-flex align-items-center flex-wrap gap-2">
        <code className="me-2">
          {filename || `Doc ${docKey}`}
        </code>

        {/* agent/state can stay as read-only badges for now */}
        {agent && (
          <span className="badge text-bg-light border small">
            Agent: {agent}
          </span>
        )}
        {state && (
          <span className="badge text-bg-light border small">
            State: {state}
          </span>
        )}

        <div className="text-muted small ms-auto d-flex align-items-center gap-2">
          {uploadedAt && (
            <span>
              <strong>Uploaded at:</strong>{" "}
              {new Date(uploadedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div className="card-body p-3">
        {/* Summary table (Doc Type / Seller / Buyer / Date) */}
        <div className="table-responsive mb-0">
          <table className="table table-sm table-bordered align-middle mb-0">
            <tbody>
              <tr>
                <th
                  style={{ width: 140, fontWeight: 600 }}
                >
                  Doc Type
                </th>
                <td>
                  <code>{doc_type || "unknown"}</code>
                </td>
                <th
                  style={{ width: 140, fontWeight: 600 }}
                >
                  Date
                </th>
                <td>
                  {/* editable acquired_date field */}
                  <InlineEditable
                    // input wants YYYY-MM-DD
                    value={displayDate ? displayDate.slice(0, 10) : ""}
                    renderValue={renderAcquiredDate}
                    onSave={(newVal) =>
                      onUpdate({
                        scope: "doc",
                        rowId: id,
                        docId: docKey,
                        field: "acquired_date",
                        value: newVal || null,
                      })
                    }
                    inputType="date"
                  />
                </td>
              </tr>
              <tr>
                <th style={{ fontWeight: 600 }}>Seller</th>
                <td>
                  <div className="small">
                    {/* Seller type toggle row */}
                    <div className="mb-2">
                      <SellerTypeToggle
                        value={seller_is_company}
                        onChange={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_is_company",
                            value: val,
                          })
                        }
                      />
                    </div>

                    <div>
                      <strong>Name:</strong>{" "}
                      <InlineEditable
                        value={seller.name}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_name",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>Address:</strong>{" "}
                      <InlineEditable
                        value={seller.address}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_address",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>City/State:</strong>{" "}
                      <InlineEditable
                        value={seller.city}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_city",
                            value: val,
                          })
                        }
                      />
                      {", "}
                      <InlineEditable
                        value={seller.state}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_state",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>ZIP:</strong>{" "}
                      <InlineEditable
                        value={seller.zip}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_zip_code",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>License #:</strong>{" "}
                      <InlineEditable
                        value={seller.license}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "seller_license_number",
                            value: val,
                          })
                        }
                      />
                    </div>
                  </div>
                </td>
                <th style={{ fontWeight: 600 }}>Buyer</th>
                <td>
                  <div className="small">
                    <div>
                      <strong>Name:</strong>{" "}
                      <InlineEditable
                        value={buyer.name}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_name",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>Address:</strong>{" "}
                      <InlineEditable
                        value={buyer.address}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_address",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>City/State:</strong>{" "}
                      <InlineEditable
                        value={buyer.city}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_city",
                            value: val,
                          })
                        }
                      />
                      {", "}
                      <InlineEditable
                        value={buyer.state}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_state",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>ZIP:</strong>{" "}
                      <InlineEditable
                        value={buyer.zip}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_zip_code",
                            value: val,
                          })
                        }
                      />
                    </div>
                    <div>
                      <strong>License #:</strong>{" "}
                      <InlineEditable
                        value={buyer.license}
                        onSave={(val) =>
                          onUpdate({
                            scope: "doc",
                            rowId: id,
                            docId: docKey,
                            field: "buyer_license_number",
                            value: val,
                          })
                        }
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Vehicles table, attached directly under summary table */}
        <div className="table-responsive" style={{ marginTop: 0 }}>
          <table
            className="table table-sm table-striped table-bordered mb-0"
            style={{ borderTop: "none" }}
          >
            <thead className="table-light">
              <tr>
                <th style={{ fontWeight: 600 }}>Make</th>
                <th style={{ fontWeight: 600 }}>Model</th>
                <th style={{ fontWeight: 600 }}>Body Style</th>
                <th style={{ fontWeight: 600 }}>Year</th>
                <th style={{ fontWeight: 600 }}>Color</th>
                <th style={{ fontWeight: 600 }}>VIN</th>
                <th style={{ fontWeight: 600 }}>Title #</th>
                <th style={{ fontWeight: 600 }}>Stock #</th>
                {hasInternal && (
                  <th style={{ fontWeight: 600 }}>Internal #</th>
                )}
              </tr>
            </thead>
            <tbody>
              {vehicles.length === 0 ? (
                <tr>
                  <td colSpan={vehicleColSpan} className="text-muted">
                    No vehicles
                  </td>
                </tr>
              ) : (
                vehicles.map((v, i) => (
                  <tr key={i}>
                    <td>
                      <InlineEditable
                        value={v.make}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "make",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.model}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "model",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.body_style}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "body_style",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.year}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "year",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.color}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "color",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td className="text-nowrap">
                      <InlineEditable
                        value={v.vin}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "vin",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.title_number}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            field: "title_number",
                            value: val,
                          })
                        }
                      />
                    </td>
                    <td>
                      <InlineEditable
                        value={v.stock_number}
                        onSave={(val) =>
                          onUpdate({
                            scope: "row",
                            rowId: v.rowId,
                            docId: docKey,
                            // scrap_inventory: Stock # is seller_stock_number
                            // individual_vehicle: Stock # is stock_number
                            field: isScrap
                              ? "seller_stock_number"
                              : "stock_number",
                            value: val,
                          })
                        }
                      />
                    </td>
                    {hasInternal && (
                      <td>
                        <InlineEditable
                          value={v.internal_number}
                          onSave={(val) =>
                            onUpdate({
                              scope: "row",
                              rowId: v.rowId,
                              docId: docKey,
                              field: "internal_number",
                              value: val,
                            })
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Card footer actions */}
        <div className="d-flex justify-content-end mt-3 gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => {
              if (!onDeleteDoc) return;
              const ok = window.confirm(
                "Delete this document and all associated titles and uploads? This cannot be undone."
              );
              if (ok) {
                onDeleteDoc(docKey);
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Generic inline editable field:
 * - Shows value in <code> with a ✏️ button.
 * - Click pencil to edit.
 * - Saves on blur or Enter via onSave(value) -> Promise.
 */
function InlineEditable({
  value,
  onSave,
  inputType = "text",
  renderValue,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!editing) {
      setDraft(value ?? "");
    }
  }, [value, editing]);

  const display = renderValue
    ? renderValue(value ?? "")
    : (
      <code>
        {value !== null && value !== undefined && value !== "" ? value : "—"}
      </code>
    );

  const handleSubmit = async () => {
    if (!onSave || draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setError("");
      setEditing(false);
    } catch (e) {
      console.error(e);
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
      setDraft(value ?? "");
    }
  };

  if (!editing) {
    return (
      <span className="d-inline-flex align-items-center">
        {display}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1"
          onClick={() => !saving && setEditing(true)}
          title="Edit"
        >
          <span aria-hidden="true">✏️</span>
        </button>
        {error && (
          <span className="text-danger small ms-2">{error}</span>
        )}
      </span>
    );
  }

  return (
    <span className="d-inline-flex align-items-center">
      <input
        type={inputType}
        className="form-control form-control-sm"
        style={{ minWidth: 80, maxWidth: 200 }}
        value={draft ?? ""}
        autoFocus
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleSubmit}
        onKeyDown={handleKeyDown}
      />
    </span>
  );
}

/**
 * Seller type toggle (Person / Company)
 * - Shows labels similar to your screenshot
 * - Click left/right half of the pill to switch
 */
function SellerTypeToggle({ value, onChange }) {
  // Normalize value coming from API: true/false, "t"/"f", "true"/"false", 1/0, etc.
  let current = null;
  if (value !== null && value !== undefined) {
    if (typeof value === "boolean") {
      current = value;
    } else if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "t"].includes(s)) current = true;
      else if (["false", "0", "no", "n", "f"].includes(s)) current = false;
    } else if (typeof value === "number") {
      current = value !== 0;
    }
  }

  const knobLeft = current === true ? "50%" : "0%";

  const handleClick = () => {
    if (!onChange) return;
    const nextVal = current === null ? true : !current;
    onChange(nextVal);
  };

  return (
    <div className="d-inline-flex flex-column">
      <div className="mb-1" style={{ fontSize: "0.75rem" }}>
        <span
          className={
            current === false ? "fw-semibold text-dark" : "text-muted"
          }
        >
          Person
        </span>
        <span className="mx-1 text-muted">|</span>
        <span
          className={
            current === true ? "fw-semibold text-dark" : "text-muted"
          }
        >
          Company
        </span>
      </div>
      <div
        className="position-relative rounded-pill border"
        style={{
          width: 70,
          height: 14,
          backgroundColor: "#f8f9fa",
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <div
          className="rounded-pill"
          style={{
            position: "absolute",
            top: 1,
            bottom: 1,
            width: "50%",
            left: knobLeft,
            transition: "left 0.2s ease",
            backgroundColor: "#28a745",
          }}
        />
      </div>
    </div>
  );
}

